import { useEffect, useState } from 'react';
import { Grid, Typography, Button, ListItemButton, CircularProgress, useTheme, darken } from '@mui/material';
import StateManager from '../../StateManager';
import CheckboxItem from '../CheckboxItem';
import { isArray } from 'lodash';
import axios from 'axios';

export default function TickboxField({ external, field, initial, onChange, editable }) {
  // for global fields
  const [loadingOptions, setLoadingOptions] = useState(false);
  // the options to be shown: for global fields - fetched options, otherwise just item options
  const [fieldOptions, setFieldOptions] = useState([]);
  // the selected options
  const [selectedValues, setSelectedValues] = useState([]);

  const theme = useTheme();

  useEffect(() => {
    setSelectedValues(isArray(initial) ? initial : []);
  }, [initial]);

  // need to run only the first time
  useEffect(() => {
    // the component shouldn't be created without 'field' prop
    if (!field) return;

    if (!editable) {
      setFieldOptions(isArray(field.options) ? field.options : []);
      return;
    }

    // for global fields - fetch the latest versions
    if (field.globalFieldId) {
      setLoadingOptions(true);
      const invitationId = localStorage.getItem('invitationId');
      axios
        .get(external ? '/external/fields/getGlobalFieldData' : '/fields/groups/getGlobalFieldData', {
          params: { globalFieldId: field.globalFieldId, invitationId },
        })
        .then(({ data }) => {
          setFieldOptions(isArray(data?.options) ? data.options : []);
        })
        .catch((err) => {
          StateManager.setAxiosErrorAlert(err);
        })
        .finally(() => {
          setLoadingOptions(false);
        });
    } else {
      // not a global field - just use options from the field
      setFieldOptions(isArray(field.options) ? field.options : []);
    }
  }, [editable]); // eslint-disable-line

  if (!field || field.fieldType !== 'tickbox') return null;

  const { displayType, selectType } = field;

  function handleOptionClick(optionId, checked) {
    if (!editable) return;
    const result =
      selectType === 'multiple'
        ? checked
          ? [...selectedValues, optionId]
          : selectedValues.filter((x) => x !== optionId)
        : checked
        ? [optionId]
        : [];
    setSelectedValues(result);
    onChange(result);
  }

  if (editable) {
    if (loadingOptions) {
      // waiting for the global fields options
      return <CircularProgress size={24} />;
    }

    if (displayType === 'buttons') {
      return (
        <Grid item container spacing={2}>
          {fieldOptions.map((option) => {
            const optionSelected = selectedValues.includes(option.id);
            const optionColor = option.color ? option.color : theme.palette.primary.main;
            const optionHoverColor = option.color ? darken(option.color, 0.2) : theme.palette.primary.dark;

            return (
              <Grid item key={option.id}>
                <Button
                  disabled={!editable}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderWidth: 2,
                    borderColor: optionColor,
                    textTransform: 'none',
                    color: optionSelected ? (theme) => theme.palette.getContrastText(optionColor) : optionColor,
                    background: optionSelected ? optionColor : '',
                    '&:hover': {
                      background: optionSelected ? optionHoverColor : '',
                      borderWidth: 2,
                      borderColor: optionHoverColor,
                    },
                  }}
                  onClick={() => handleOptionClick(option.id, !optionSelected)}
                >
                  {option.text}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      );
    }

    if (displayType === 'images') {
      return (
        <Grid item container>
          {fieldOptions.map((option) => {
            const optionSelected = selectedValues.includes(option.id);
            return (
              <Grid item container xs={2} sx={{ p: 0.25 }} key={option.id}>
                <Grid
                  item
                  container
                  component={ListItemButton}
                  sx={{
                    borderRadius: 1,
                    p: 0.5,
                    border: optionSelected ? (theme) => `2px ${theme.palette.primary.main} solid` : `2px inherit solid`,
                  }}
                  alignContent={'flex-start'}
                  onClick={() => handleOptionClick(option.id, !optionSelected)}
                  flexDirection={'column'}
                >
                  {option.image && (
                    <Grid item component={'img'} src={option.image} alt={option.text} sx={{ maxWidth: '100%' }} />
                  )}
                  <Grid item container justifyContent={'center'} sx={{ textAlign: 'center', pt: 1, mt: 'auto' }}>
                    <Typography>{option.text}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      );
    }

    const notNested = fieldOptions.filter((x) => !x.parentId);

    return (
      <Grid container>
        <Grid container>
          <Typography gutterBottom color={'text.secondary'}>
            Select value{selectType === 'multiple' ? 's' : ''}
          </Typography>
        </Grid>
        <Grid container>
          {notNested.map((option) => (
            <CheckboxItem
              key={option.id}
              option={option}
              editable={editable}
              allOptions={fieldOptions}
              onChange={handleOptionClick}
              checkedItems={selectedValues}
            />
          ))}
        </Grid>
      </Grid>
    );
  }

  // the whole selectedOptions can be included in the field - important for global field
  const selected = isArray(field.filledValue?.selectedOptions)
    ? field.filledValue.selectedOptions
    : fieldOptions.filter((o) => selectedValues.includes(o.id));

  // display as images - render as tiles
  if (displayType === 'images') {
    return (
      <Grid container>
        {selected.map((option, index) => (
          <Grid item container xs={2} sx={{ p: 0.25 }} key={index}>
            <Grid item container alignContent={'flex-start'}>
              {option.image && (
                <Grid item container component={'img'} src={option.image} alt={option.text} sx={{ maxWidth: '100%' }} />
              )}
            </Grid>
            <Grid item container justifyContent={'center'} sx={{ textAlign: 'center', pt: 1, mt: 'auto' }}>
              <Typography>{option.text}</Typography>
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container>
      {selected.map((option, index) => (
        <Grid container item key={index} sx={{ my: 0.5 }}>
          <Typography>{option.text}</Typography>
        </Grid>
      ))}
    </Grid>
  );
}
